import { useCallback, useEffect, useRef, useState } from 'react';

import { useDropdownContext } from '~/components/Dropdown/hooks/useDropdownContext';
import { Spinner } from '~/components/Spinner';
import { cn } from '~/lib/cn';

import { useItemEventHandlers } from '../../hooks/useItemEventHandlers';
import { DropdownItem } from '../DropdownItem';

interface DropdownContentProps {
  children?: React.ReactNode;
  className?: string;
}

export function DropdownContent({ children, className }: DropdownContentProps) {
  const {
    collection,
    selected,
    multiselect,
    isSelected,
    maxSelectedOptions,
    loadMoreDistance,
    onLoadMore,
  } = useDropdownContext();
  const createHandlers = useItemEventHandlers();

  const canSelectMore =
    !multiselect ||
    maxSelectedOptions === undefined ||
    selected.length < maxSelectedOptions;

  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleLoadMore = useCallback(async () => {
    if (!onLoadMore || loading) {
      return;
    }

    setLoading(true);
    await onLoadMore();
    setLoading(false);
  }, [onLoadMore, loading]);

  useEffect(() => {
    if (!sentinelRef.current || !containerRef.current || !onLoadMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;

        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: containerRef.current,
        rootMargin: `${loadMoreDistance}px`,
        threshold: 0,
      },
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [handleLoadMore, loadMoreDistance, onLoadMore]);

  return (
    <div
      ref={containerRef}
      role="listbox"
      onWheel={(e) => e.stopPropagation()}
      className={cn([
        'rounded-xs',
        'bg-level-01/full',
        'w-[var(--radix-popover-trigger-width)]',
        'mt-xs',
        'overflow-auto',
        'shadow',
        'border',
        'border-emphasis-strong',
        className,
      ])}
    >
      {children}
      {collection.map(({ label, value, ref }) => (
        <DropdownItem
          key={value}
          checked={isSelected(value)}
          isCheckbox={multiselect}
          disabled={canSelectMore ? false : !isSelected(value)}
          {...createHandlers(value)}
          ref={ref}
        >
          {label}
        </DropdownItem>
      ))}
      {onLoadMore ? <div ref={sentinelRef} /> : null}
      {loading ? (
        <div className="flex justify-center px-md py-sm text-white">
          <Spinner size="sm" />
        </div>
      ) : null}
    </div>
  );
}

DropdownContent.displayName = 'DropdownContent';
